import {tagListData} from '~/components/Controls/Tags';
import {formatJsonValue} from '~/lib/json';
import {AuctionType, defaultBidder, IBidder, PlacementFormatsTable} from './Bidder';
import {BidderFormValues} from './BidderFormSchema';

export type GroupIdValue = number | {id?: number; value?: number} | null | undefined;

const seatEditorValue = (item: unknown) => {
    if (Array.isArray(item)) {
        return item.join('\n');
    }
    if (typeof item === 'string') {
        return item;
    }
    return '';
};

const nullFilter = (item: unknown) => item === null || item === undefined ? '' : item;

export const normalizeGroupIds = (groupIds?: GroupIdValue[]) => (groupIds ?? [])
    .map(groupId => {
        if (typeof groupId === 'number') {
            return groupId;
        }
        if (groupId && typeof groupId === 'object') {
            if (typeof groupId.id === 'number') {
                return groupId.id;
            }
            if (typeof groupId.value === 'number') {
                return groupId.value;
            }
        }
        return null;
    })
    .filter((id): id is number => typeof id === 'number');

const normalizeImpExpirationTime = (value: BidderFormValues['impExpirationTime']) => {
    if (value === null || value === undefined || value === '') {
        return undefined;
    }
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : undefined;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
};

const normalizeMockBidResponse = (value: BidderFormValues['mockBidResponse']) =>
    value?.trim() === '' || value === null ? undefined : value;

export const toBidderFormValues = (bidder?: IBidder): BidderFormValues => {
    const sourceBidder = bidder ?? defaultBidder;

    return {
        check: sourceBidder.check ?? defaultBidder.check,
        id: sourceBidder.id ?? defaultBidder.id,
        agencyId: sourceBidder.agencyId ?? defaultBidder.agencyId,
        title: sourceBidder.title ?? defaultBidder.title,
        tags: Array.isArray(sourceBidder.tags) ? sourceBidder.tags : defaultBidder.tags,
        endpoint: sourceBidder.endpoint ?? defaultBidder.endpoint,
        coppaFlag: sourceBidder.coppaFlag ?? defaultBidder.coppaFlag,
        excludeFromNonInBannerAuction: sourceBidder.excludeFromNonInBannerAuction ?? defaultBidder.excludeFromNonInBannerAuction,
        countries: Array.isArray(sourceBidder.countries) ? sourceBidder.countries : defaultBidder.countries,
        platforms: Array.isArray(sourceBidder.platforms) ? sourceBidder.platforms : defaultBidder.platforms,
        maxRpm: sourceBidder.maxRpm ?? defaultBidder.maxRpm,
        adControl: sourceBidder.adControl ?? defaultBidder.adControl,
        includedSellers: Array.isArray(sourceBidder.includedSellers) ? sourceBidder.includedSellers : defaultBidder.includedSellers,
        protocol: sourceBidder.protocol ?? defaultBidder.protocol,
        encoding: sourceBidder.encoding ?? null,
        auctionType: sourceBidder.auctionType ?? AuctionType.FIRST,
        delayedNotification: sourceBidder.delayedNotification ?? defaultBidder.delayedNotification,
        wseat: seatEditorValue(sourceBidder.wseat),
        bseat: seatEditorValue(sourceBidder.bseat),
        minBidFloor: nullFilter(sourceBidder.minBidFloor) as BidderFormValues['minBidFloor'],
        maxBidFloor: nullFilter(sourceBidder.maxBidFloor) as BidderFormValues['maxBidFloor'],
        floorType: sourceBidder.floorType ?? defaultBidder.floorType,
        noFloor: sourceBidder.noFloor ?? defaultBidder.noFloor,
        skAnIdCheck: sourceBidder.skAnIdCheck ?? defaultBidder.skAnIdCheck,
        skAnId: sourceBidder.skAnId ?? defaultBidder.skAnId,
        dc: Array.isArray(sourceBidder.dc) ? sourceBidder.dc : defaultBidder.dc,
        spendCapping: sourceBidder.spendCapping ?? defaultBidder.spendCapping,
        impExpirationTime: nullFilter(sourceBidder.impExpirationTime) as BidderFormValues['impExpirationTime'],
        status: sourceBidder.status ?? defaultBidder.status,
        mockBidResponse: formatJsonValue(sourceBidder.mockBidResponse),
        useAdMergeType: sourceBidder.useAdMergeType ?? defaultBidder.useAdMergeType,
        auctionCategory: sourceBidder.auctionCategory ?? defaultBidder.auctionCategory,
        clientNurl: sourceBidder.clientNurl ?? defaultBidder.clientNurl,
        clientLurl: sourceBidder.clientLurl ?? defaultBidder.clientLurl,
        passCalloutRound: sourceBidder.passCalloutRound ?? defaultBidder.passCalloutRound,
        tagIdRequirement: sourceBidder.tagIdRequirement ?? defaultBidder.tagIdRequirement,
        gpidRequirement: sourceBidder.gpidRequirement ?? defaultBidder.gpidRequirement,
        healthyAvgPrice: sourceBidder.healthyAvgPrice ?? null,
        placementFormats: sourceBidder.placementFormats ?? defaultBidder.placementFormats,
        multiFormatRequest: sourceBidder.multiFormatRequest ?? defaultBidder.multiFormatRequest,
    };
};

const normalizePlacementFormats = (table: IBidder['placementFormats']): PlacementFormatsTable | null => {
    if (!table) {
        return null;
    }
    const entries = Object.entries(table).filter(([, formats]) => !!formats?.length);
    return entries.length ? Object.fromEntries(entries) as PlacementFormatsTable : null;
};

export const toBidderSubmitValues = (values: BidderFormValues): IBidder => ({
    ...values,
    auctionType: values.auctionType ?? AuctionType.FIRST,
    encoding: values.encoding ?? undefined,
    minBidFloor: values.minBidFloor === '' ? null : values.minBidFloor,
    maxBidFloor: values.maxBidFloor === '' ? null : values.maxBidFloor,
    impExpirationTime: normalizeImpExpirationTime(values.impExpirationTime),
    mockBidResponse: normalizeMockBidResponse(values.mockBidResponse),
    wseat: tagListData(values.wseat),
    bseat: tagListData(values.bseat),
    healthyAvgPrice: values.healthyAvgPrice === '' ? null : values.healthyAvgPrice,
    placementFormats: normalizePlacementFormats(values.placementFormats),
    multiFormatRequest: !!values.multiFormatRequest,
});